package com.zi.support.shared

/** A field an admin can fill in; [choices] makes it a pick-list (e.g. a support user's role), [secret] a password box. */
data class DirectoryField(val key: String, val label: String, val choices: List<String> = emptyList(), val secret: Boolean = false)

/**
 * One list on the Settings screen, served by the manageDirectory Cloud Function (functions/lib/directory.js
 * holds the matching server rules). Rows arrive as {id, values}; [titleKey] and [subtitleKeys] pick what
 * each row shows, [idField] and [addFields] are asked only when adding, and [fields] are what admins can edit.
 */
data class SettingsDirectory(
    val id: String,
    val label: String,
    val titleKey: String,
    val subtitleKeys: List<String>,
    val idField: DirectoryField? = null,
    val fields: List<DirectoryField> = emptyList(),
    val addFields: List<DirectoryField> = emptyList(),
    val canAdd: Boolean = false,
    val canDelete: Boolean = false,
    val readOnly: Boolean = false,
    val canChangePassword: Boolean = false,
    /** Rows get an Invite button (manageDirectory "invite": emails the customer portal link). */
    val canInvite: Boolean = false,
    val emptyText: String = "Nothing here yet.",
    /** Lists that grow forever (emails) page by this time value instead of loading whole. */
    val timeKey: String? = null,
) {
    /** What the add/edit form asks, in order. */
    fun formFields(adding: Boolean): List<DirectoryField> =
        (if (adding) listOfNotNull(idField) + addFields else emptyList()) + fields
}

/** A Settings filter button; tabs with more than one directory show a second row of buttons. */
data class SettingsTab(val label: String, val directories: List<SettingsDirectory>)

object SettingsDirectories {
    val tabs: List<SettingsTab> = listOf(
        SettingsTab("Support users", listOf(
            SettingsDirectory("supportUsers", "Support users", "username", listOf("email", "role"),
                idField = DirectoryField("username", "Username (they sign in with this)"),
                addFields = listOf(DirectoryField("password", "Password (at least 6 characters)", secret = true)),
                fields = listOf(DirectoryField("role", "Role", listOf("user", "admin")), DirectoryField("email", "Real email (optional)")), canAdd = true, canChangePassword = true),
        )),
        SettingsTab("Customers", listOf(
            // Grouped by company on the server (customerEmails/{companyId}/addresses); the company is fixed once added.
            SettingsDirectory("customerEmails", "Emails", "email", listOf("name", "companyId", "access", "phone", "lastTicketNumber"),
                idField = DirectoryField("email", "Email"),
                addFields = listOf(DirectoryField("companyId", "Company ID (blank = from the email's domain)")),
                fields = listOf(DirectoryField("name", "Name"), DirectoryField("phone", "Phone"),
                    DirectoryField("access", "Portal access: none, own tickets or all company tickets", listOf("none", "own", "company"))),
                canAdd = true, canDelete = true, canInvite = true, emptyText = "No customer emails yet."),
            SettingsDirectory("customerCalls", "Phones", "phone", listOf("name", "email", "companyId", "lastTicketNumber"),
                idField = DirectoryField("phone", "Phone"),
                fields = listOf(DirectoryField("name", "Name"), DirectoryField("email", "Email"), DirectoryField("companyId", "Company ID")),
                canAdd = true, canDelete = true, emptyText = "No customer phone numbers yet."),
            SettingsDirectory("companies", "Companies", "companyName", listOf("companyId"),
                idField = DirectoryField("companyId", "Company ID (e.g. gantrans)"),
                fields = listOf(DirectoryField("companyName", "Company name")),
                canAdd = true, canDelete = true, emptyText = "No companies yet."),
        )),
        SettingsTab("Domains", listOf(
            SettingsDirectory("customerDomains", "Allowed domains", "domain", listOf("customerName", "companyId"),
                idField = DirectoryField("domain", "Domain (e.g. gantrans.net.za)"),
                fields = listOf(DirectoryField("customerName", "Customer name"), DirectoryField("companyId", "Company ID")),
                canAdd = true, canDelete = true, emptyText = "No allowed domains: nobody can use the portal or email in tickets."),
        )),
        SettingsTab("Emails", listOf(
            SettingsDirectory("mail", "Outgoing", "subject", listOf("to", "state", "sentAt", "error"), readOnly = true, emptyText = "No emails sent yet.", timeKey = "createdAt"),
            SettingsDirectory("inboundEmails", "Incoming", "subject", listOf("from", "outcome", "ticketNumber", "receivedAt"), readOnly = true,
                emptyText = "No incoming emails recorded yet. Emails are recorded from this update onwards.", timeKey = "receivedAt"),
        )),
    )

    private val ACCESS = mapOf("none" to "Portal: domain only", "own" to "Portal: own tickets", "company" to "Portal: all company tickets")
    private val ISO = Regex("""^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})""")
    private val MONTHS = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    private val OUTCOMES = mapOf(
        "ticket_created" to "Ticket created", "duplicate" to "Already had a ticket",
        "domain_not_allowed" to "Sender not allowed", "skipped_automated" to "Skipped (auto-reply)",
        "reply_added" to "Added to its ticket", "support_reply_added" to "Our reply added to its ticket",
    )

    /** How a stored value reads in a row: UTC timestamps in SAST, email outcomes in words. */
    fun display(key: String, value: String): String {
        if (key == "access") ACCESS[value]?.let { return it }
        OUTCOMES[value]?.let { return it }
        val match = ISO.find(value) ?: return value
        val (y, mo, d, h, mi) = match.destructured
        // Africa/Johannesburg is UTC+2 all year.
        var year = y.toInt(); var month = mo.toInt(); var day = d.toInt(); var hour = h.toInt() + 2
        if (hour >= 24) {
            hour -= 24; day += 1
            val leap = year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)
            val days = intArrayOf(31, if (leap) 29 else 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)[month - 1]
            if (day > days) { day = 1; month += 1; if (month > 12) { month = 1; year += 1 } }
        }
        return "$day ${MONTHS[month - 1]} $year, ${hour.toString().padStart(2, '0')}:$mi"
    }

    fun title(directory: SettingsDirectory, id: String, values: Map<String, String>): String =
        values[directory.titleKey]?.takeIf { it.isNotBlank() } ?: id

    fun subtitle(directory: SettingsDirectory, values: Map<String, String>): String =
        directory.subtitleKeys.mapNotNull { key -> values[key]?.takeIf { it.isNotBlank() }?.let { display(key, it) } }.joinToString(" · ")
}

data class DirectoryRow(val id: String, val values: Map<String, String>)

/**
 * What Settings has already loaded this session, so lists aren't re-read every time a tab opens.
 * Small lists are loaded once (until an edit or Refresh); email lists ([SettingsDirectory.timeKey]) show the
 * newest [EMAIL_LIMIT] and only ask the server for ones newer than those.
 */
object DirectoryCache {
    const val EMAIL_LIMIT: Int = 50

    private class Entry(val rows: MutableMap<String, DirectoryRow> = linkedMapOf(), var canEdit: Boolean = false, var isAdmin: Boolean = false)
    private val entries = mutableMapOf<String, Entry>()

    fun isLoaded(directory: SettingsDirectory): Boolean = directory.id in entries

    /** Whether opening the tab needs a server call: first load, or an email list checking for newer mail. */
    fun needsLoad(directory: SettingsDirectory): Boolean = !isLoaded(directory) || directory.timeKey != null

    /** Extra list parameters: newerThan for an email list that already has rows, else none. */
    fun request(directory: SettingsDirectory): Map<String, String> {
        val key = directory.timeKey ?: return emptyMap()
        val newest = entries[directory.id]?.rows?.values?.mapNotNull { it.values[key]?.takeIf { v -> v.isNotBlank() } }.orEmpty().maxOrNull()
        return if (newest == null) emptyMap() else mapOf("newerThan" to newest)
    }

    fun store(directory: SettingsDirectory, rows: List<DirectoryRow>, canEdit: Boolean, isAdmin: Boolean) {
        val entry = entries.getOrPut(directory.id) { Entry() }
        if (directory.timeKey == null) entry.rows.clear()
        rows.forEach { entry.rows[it.id] = it }
        directory.timeKey?.let { key ->
            // Emails: keep only the newest EMAIL_LIMIT.
            val keep = entry.rows.values.sortedByDescending { it.values[key].orEmpty() }.take(EMAIL_LIMIT).map { it.id }.toSet()
            entry.rows.keys.retainAll(keep)
        }
        entry.canEdit = canEdit; entry.isAdmin = isAdmin
    }

    fun forget(directory: SettingsDirectory) { entries.remove(directory.id) }

    /** Rows to show: email lists newest first, everything else by title. */
    fun rows(directory: SettingsDirectory): List<DirectoryRow> {
        val rows = entries[directory.id]?.rows?.values?.toList().orEmpty()
        val key = directory.timeKey
        return if (key != null) rows.sortedByDescending { it.values[key].orEmpty() }
        else rows.sortedBy { SettingsDirectories.title(directory, it.id, it.values).lowercase() }
    }

    fun canEdit(directory: SettingsDirectory): Boolean = entries[directory.id]?.canEdit == true
    fun isAdmin(directory: SettingsDirectory): Boolean = entries[directory.id]?.isAdmin == true
}
